import React, { useMemo } from 'react';
import { Badge, Box, HStack, Text } from '@chakra-ui/react';

/**
 * Classic O(m*n) LCS diff, returning line-level ops. Email content is
 * small enough that the straightforward implementation stays clear and
 * predictable.
 */
const computeLineDiff = (beforeText, afterText) => {
  const before = (beforeText ?? '').split(/\r?\n/);
  const after = (afterText ?? '').split(/\r?\n/);
  const m = before.length;
  const n = after.length;

  const lcs = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = m - 1; i >= 0; i -= 1) {
    for (let j = n - 1; j >= 0; j -= 1) {
      if (before[i] === after[j]) {
        lcs[i][j] = lcs[i + 1][j + 1] + 1;
      } else {
        lcs[i][j] = Math.max(lcs[i + 1][j], lcs[i][j + 1]);
      }
    }
  }

  const ops = [];
  let i = 0;
  let j = 0;
  while (i < m && j < n) {
    if (before[i] === after[j]) {
      ops.push({ kind: 'same', line: before[i] });
      i += 1;
      j += 1;
    } else if (lcs[i + 1][j] >= lcs[i][j + 1]) {
      ops.push({ kind: 'remove', line: before[i] });
      i += 1;
    } else {
      ops.push({ kind: 'add', line: after[j] });
      j += 1;
    }
  }
  while (i < m) {
    ops.push({ kind: 'remove', line: before[i] });
    i += 1;
  }
  while (j < n) {
    ops.push({ kind: 'add', line: after[j] });
    j += 1;
  }
  return ops;
};

const DIFF_LINE_STYLES = {
  same: {
    bg: 'transparent',
    color: 'rgba(255,255,255,0.78)',
    prefix: ' ',
    borderColor: 'transparent',
  },
  add: {
    bg: 'rgba(52,211,153,0.10)',
    color: '#86efac',
    prefix: '+',
    borderColor: 'rgba(52,211,153,0.35)',
  },
  remove: {
    bg: 'rgba(239,68,68,0.10)',
    color: '#fca5a5',
    prefix: '-',
    borderColor: 'rgba(239,68,68,0.35)',
  },
};

const ComplianceContentDiff = ({ title, beforeText, afterText }) => {
  const ops = useMemo(
    () => computeLineDiff(beforeText, afterText),
    [beforeText, afterText],
  );
  const hasAnyChange = ops.some((op) => op.kind !== 'same');

  return (
    <Box>
      <HStack spacing={2} mb={2} align="center">
        <Text
          fontSize="11px"
          letterSpacing="0.04em"
          textTransform="uppercase"
          fontWeight="600"
          color="rgba(255,255,255,0.52)"
        >
          {title}
        </Text>
        {!hasAnyChange && (
          <Badge
            bg="rgba(255,255,255,0.06)"
            color="rgba(255,255,255,0.62)"
            border="1px solid"
            borderColor="rgba(255,255,255,0.12)"
            fontSize="10px"
            textTransform="none"
            fontWeight="500"
            px={1.5}
            borderRadius="full"
          >
            No changes since review
          </Badge>
        )}
      </HStack>

      {hasAnyChange ? (
        <Box
          bg="rgba(255,255,255,0.02)"
          border="1px solid"
          borderColor="rgba(255,255,255,0.08)"
          borderRadius="8px"
          py={2}
          px={0}
          maxH="260px"
          overflowY="auto"
        >
          {ops.map((op, idx) => {
            const style = DIFF_LINE_STYLES[op.kind];
            return (
              <Box
                key={`${op.kind}-${idx}`}
                px={3}
                py={0.5}
                bg={style.bg}
                borderLeft="2px solid"
                borderColor={style.borderColor}
                fontFamily="ui-monospace, SFMono-Regular, Menlo, Consolas, monospace"
                fontSize="12.5px"
                lineHeight="1.55"
                color={style.color}
                whiteSpace="pre-wrap"
                wordBreak="break-word"
              >
                <Box as="span" opacity={0.6} mr={2} userSelect="none">
                  {style.prefix}
                </Box>
                {op.line || '\u00a0'}
              </Box>
            );
          })}
        </Box>
      ) : (
        <Box
          bg="rgba(255,255,255,0.02)"
          border="1px solid"
          borderColor="rgba(255,255,255,0.08)"
          borderRadius="8px"
          py={3}
          px={3}
          maxH="260px"
          overflowY="auto"
        >
          {(afterText ?? '').trim() ? (
            <Text
              fontSize="13px"
              color="rgba(255,255,255,0.72)"
              whiteSpace="pre-wrap"
              wordBreak="break-word"
              lineHeight="1.6"
            >
              {afterText}
            </Text>
          ) : (
            <Text fontSize="xs" color="rgba(255,255,255,0.32)" fontStyle="italic">
              (empty)
            </Text>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ComplianceContentDiff;
